import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Switch,
  FormControlLabel,
  TextField,
  Button,
  Grid,
  Chip,
  Alert,
  CircularProgress,
  Divider,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  PlayArrow as TestIcon,
  HealthAndSafety as HealthIcon,
  RotateRight as RotateIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { useProxyConfig, useProxyStatus } from '../hooks/useProxyConfig';
import { useOutboundIp } from '../hooks/useOutboundIp';
import { ProxyConfigUpdate } from '../types/proxy';

export const ProxyConfig: React.FC = () => {
  const {
    config,
    isLoading,
    error,
    updateConfig,
    isUpdating,
    testConfig,
    isTesting,
    testResults,
    healthCheck,
    isHealthChecking,
    rotateProxies,
    isRotating,
    reloadConfig,
    isReloading,
  } = useProxyConfig();

  const { status, refetch: refetchStatus } = useProxyStatus();

  const {
    outboundIp,
    isLoading: isOutboundIpLoading,
    error: outboundIpError,
    refetch: refetchOutboundIp,
  } = useOutboundIp();

  const [formData, setFormData] = useState<ProxyConfigUpdate>({
    enabled: false,
    rotation_enabled: false,
    rotation_strategy: 'round_robin',
    proxy_list: [],
    username: '',
    password: '',
    health_check_url: 'http://httpbin.org/ip',
    timeout: 30,
    max_retries: 3,
    health_check_interval: 300,
  });

  const [newProxy, setNewProxy] = useState('');

  // Update form data when config changes
  React.useEffect(() => {
    if (config) {
      setFormData({
        enabled: config.enabled,
        rotation_enabled: config.rotation_enabled,
        rotation_strategy: config.rotation_strategy,
        proxy_list: config.proxy_list,
        username: config.username || '',
        password: config.password || '',
        health_check_url: config.health_check_url,
        timeout: config.timeout,
        max_retries: config.max_retries,
        health_check_interval: config.health_check_interval,
      });
    }
  }, [config]);

  const handleSave = () => {
    if (config) {
      updateConfig(formData);
    }
  };

  const handleAddProxy = () => {
    if (newProxy.trim() && !formData.proxy_list.includes(newProxy.trim())) {
      setFormData(prev => ({
        ...prev,
        proxy_list: [...prev.proxy_list, newProxy.trim()],
      }));
      setNewProxy('');
    }
  };

  const handleRemoveProxy = (proxyToRemove: string) => {
    setFormData(prev => ({
      ...prev,
      proxy_list: prev.proxy_list.filter(proxy => proxy !== proxyToRemove),
    }));
  };

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" p={3}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error">
        Error loading proxy configuration: {(error as any).message || 'Unknown error'}
      </Alert>
    );
  }

  return (
    <Box>
      <Typography variant="h5" gutterBottom>
        Proxy Configuration
      </Typography>

      <Grid container spacing={3}>
        {/* Main Configuration */}
        <Grid item xs={12} md={8}>
          <Card>
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                <Typography variant="h6">Settings</Typography>
                <Box textAlign="right">
                  <Typography variant="caption" color="text.secondary" display="block">
                    Outbound IP (for proxy whitelisting):
                  </Typography>
                  {isOutboundIpLoading && (
                    <Typography variant="body2" color="text.secondary">
                      Detecting outbound IP...
                    </Typography>
                  )}
                  {outboundIpError && (
                    <Typography variant="body2" color="error">
                      Failed to detect IP{' '}
                      <Button size="small" onClick={() => refetchOutboundIp()}>
                        Retry
                      </Button>
                    </Typography>
                  )}
                  {outboundIp && !outboundIpError && (
                    <Typography variant="body2" color="primary">
                      {outboundIp.ip_address}{' '}
                      <Button size="small" onClick={() => refetchOutboundIp()}>
                        Refresh
                      </Button>
                    </Typography>
                  )}
                </Box>
              </Box>

              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.enabled}
                        onChange={e =>
                          setFormData(prev => ({ ...prev, enabled: e.target.checked }))
                        }
                      />
                    }
                    label="Enable Proxy Rotation"
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.rotation_enabled}
                        onChange={e =>
                          setFormData(prev => ({ ...prev, rotation_enabled: e.target.checked }))
                        }
                        disabled={!formData.enabled}
                      />
                    }
                    label="Enable Rotation"
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <InputLabel>Rotation Strategy</InputLabel>
                    <Select
                      value={formData.rotation_strategy}
                      onChange={e =>
                        setFormData(prev => ({ ...prev, rotation_strategy: e.target.value as any }))
                      }
                      disabled={!formData.enabled || !formData.rotation_enabled}
                    >
                      <MenuItem value="round_robin">Round Robin</MenuItem>
                      <MenuItem value="random">Random</MenuItem>
                      <MenuItem value="failover">Failover</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Health Check URL"
                    value={formData.health_check_url}
                    onChange={e =>
                      setFormData(prev => ({ ...prev, health_check_url: e.target.value }))
                    }
                    disabled={!formData.enabled}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Timeout (seconds)"
                    value={formData.timeout}
                    onChange={e =>
                      setFormData(prev => ({ ...prev, timeout: parseInt(e.target.value) || 30 }))
                    }
                    disabled={!formData.enabled}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Max Retries"
                    value={formData.max_retries}
                    onChange={e =>
                      setFormData(prev => ({ ...prev, max_retries: parseInt(e.target.value) || 3 }))
                    }
                    disabled={!formData.enabled}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Health Check Interval (seconds)"
                    value={formData.health_check_interval}
                    onChange={e =>
                      setFormData(prev => ({
                        ...prev,
                        health_check_interval: parseInt(e.target.value) || 300,
                      }))
                    }
                    disabled={!formData.enabled}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Username (optional)"
                    value={formData.username}
                    onChange={e => setFormData(prev => ({ ...prev, username: e.target.value }))}
                    disabled={!formData.enabled}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    type="password"
                    label="Password (optional)"
                    value={formData.password}
                    onChange={e => setFormData(prev => ({ ...prev, password: e.target.value }))}
                    disabled={!formData.enabled}
                  />
                </Grid>
              </Grid>

              <Box mt={2}>
                <Button
                  variant="contained"
                  onClick={handleSave}
                  disabled={isUpdating}
                  startIcon={isUpdating ? <CircularProgress size={20} /> : null}
                >
                  {isUpdating ? 'Saving...' : 'Save Configuration'}
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Proxy List */}
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Proxy Servers
              </Typography>

              <Box mb={2}>
                <Grid container spacing={1}>
                  <Grid item xs={8}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Add Proxy"
                      value={newProxy}
                      onChange={e => setNewProxy(e.target.value)}
                      placeholder="http://host:port"
                      disabled={!formData.enabled}
                    />
                  </Grid>
                  <Grid item xs={4}>
                    <Button
                      fullWidth
                      variant="outlined"
                      onClick={handleAddProxy}
                      disabled={!newProxy.trim() || !formData.enabled}
                      startIcon={<AddIcon />}
                    >
                      Add
                    </Button>
                  </Grid>
                </Grid>
              </Box>

              <Box>
                {formData.proxy_list.map((proxy, index) => (
                  <Chip
                    key={index}
                    label={proxy}
                    onDelete={() => handleRemoveProxy(proxy)}
                    deleteIcon={<DeleteIcon />}
                    sx={{ m: 0.5 }}
                  />
                ))}
                {formData.proxy_list.length === 0 && (
                  <Typography variant="body2" color="text.secondary">
                    No proxies configured
                  </Typography>
                )}
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Actions */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Actions
              </Typography>

              <Box display="flex" gap={2} flexWrap="wrap">
                <Button
                  variant="outlined"
                  onClick={() => testConfig()}
                  disabled={isTesting || !formData.enabled}
                  startIcon={isTesting ? <CircularProgress size={20} /> : <TestIcon />}
                >
                  {isTesting ? 'Testing...' : 'Test Proxies'}
                </Button>

                <Button
                  variant="outlined"
                  onClick={() => healthCheck()}
                  disabled={isHealthChecking || !formData.enabled}
                  startIcon={isHealthChecking ? <CircularProgress size={20} /> : <HealthIcon />}
                >
                  {isHealthChecking ? 'Checking...' : 'Health Check'}
                </Button>

                <Button
                  variant="outlined"
                  onClick={() => rotateProxies()}
                  disabled={isRotating || !formData.enabled}
                  startIcon={isRotating ? <CircularProgress size={20} /> : <RotateIcon />}
                >
                  {isRotating ? 'Rotating...' : 'Rotate Proxies'}
                </Button>

                <Button
                  variant="outlined"
                  onClick={() => reloadConfig()}
                  disabled={isReloading}
                  startIcon={isReloading ? <CircularProgress size={20} /> : <RefreshIcon />}
                >
                  {isReloading ? 'Reloading...' : 'Reload Config'}
                </Button>

                <Button
                  variant="outlined"
                  onClick={() => refetchStatus()}
                  startIcon={<RefreshIcon />}
                >
                  Refresh Status
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Test Results */}
        {testResults && (
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Test Results
                </Typography>

                <Grid container spacing={2}>
                  <Grid item xs={12} sm={4}>
                    <Typography variant="body2" color="text.secondary">
                      Total Proxies: {testResults.data.total_proxies}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Typography variant="body2" color="success.main">
                      Successful: {testResults.data.successful}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Typography variant="body2" color="error.main">
                      Failed: {testResults.data.failed}
                    </Typography>
                  </Grid>
                </Grid>

                <Divider sx={{ my: 2 }} />

                <Box>
                  {testResults.data.test_results.map((result, index) => (
                    <Chip
                      key={index}
                      label={`${result.proxy} - ${result.status}`}
                      color={result.status === 'success' ? 'success' : 'error'}
                      variant="outlined"
                      sx={{ m: 0.5 }}
                    />
                  ))}
                </Box>
              </CardContent>
            </Card>
          </Grid>
        )}

        {/* Status */}
        {status && (
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Proxy Status
                </Typography>

                <Grid container spacing={2}>
                  <Grid item xs={12} sm={3}>
                    <Typography variant="body2" color="text.secondary">
                      Enabled: {status.data.enabled ? 'Yes' : 'No'}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={3}>
                    <Typography variant="body2" color="text.secondary">
                      Total: {status.data.total}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={3}>
                    <Typography variant="body2" color="success.main">
                      Healthy: {status.data.healthy}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={3}>
                    <Typography variant="body2" color="error.main">
                      Unhealthy: {status.data.unhealthy}
                    </Typography>
                  </Grid>
                </Grid>

                {status.data.total > 0 && (
                  <>
                    <Divider sx={{ my: 2 }} />
                    <Typography variant="body2" color="text.secondary">
                      Strategy: {status.data.rotation_strategy}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Avg Response Time: {status.data.avg_response_time}s
                    </Typography>
                  </>
                )}
              </CardContent>
            </Card>
          </Grid>
        )}
      </Grid>
    </Box>
  );
};
